/**
 * @fileoverview ClubOS Player Comparison page — side-by-side attribute comparison for up to 4 players
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Mock data
import { scoutReports } from "data/mockData";

// India locale
import { formatCompact } from "utils/indiaLocale";

// TODO: Replace with API call when backend is ready
// import { scoutingService } from "api";

const MAX_PLAYERS = 4;

const ATTRIBUTES = [
  { key: "technical",  label: "Technical" },
  { key: "physical",   label: "Physical" },
  { key: "tactical",   label: "Tactical" },
  { key: "mental",     label: "Mental" },
  { key: "potential",  label: "Potential" },
];

const PLAYER_COLORS = ["#17c1e8", "#82d616", "#ea0606", "#fb8c00"];

const RECOMMENDATION_COLOR = {
  "Sign Now": "success",
  "Monitor": "info",
  "Revisit": "warning",
  "Pass": "error",
};

function PlayerComparison() {
  const [selected, setSelected] = useState([scoutReports[0]?.id ?? null, scoutReports[1]?.id ?? null, null, null]);

  // TODO: Uncomment when API is ready
  // const [allReports, setAllReports] = useState(scoutReports);
  // useEffect(() => { scoutingService.getReports().then(setAllReports).catch(console.error); }, []);

  const players = selected.map((id) => (id ? scoutReports.find((r) => r.id === id) : null));

  const activePlayers = players.filter(Boolean);

  const handleSelect = (slotIndex, value) => {
    setSelected((prev) => {
      const next = [...prev];
      next[slotIndex] = value === "" ? null : Number(value);
      return next;
    });
  };

  const getBestForAttr = (key) => {
    const vals = activePlayers.map((p) => p.attributes?.[key] ?? 0);
    return Math.max(...vals);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Player Comparison
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Compare up to 4 players side by side · FC Metropolitan Scouting
          </SoftTypography>
        </SoftBox>

        {/* Player selectors */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={2.5}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                Select Players
              </SoftTypography>
              <Grid container spacing={2}>
                {Array.from({ length: MAX_PLAYERS }).map((_, i) => (
                  <Grid key={i} item xs={6} sm={3}>
                    <SoftBox
                      sx={{
                        borderTop: `3px solid ${PLAYER_COLORS[i]}`,
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <FormControl fullWidth size="small">
                        <Select
                          value={selected[i] ?? ""}
                          onChange={(e) => handleSelect(i, e.target.value)}
                          displayEmpty
                          sx={{ borderRadius: "0 0 8px 8px" }}
                        >
                          <MenuItem value="">
                            <em style={{ color: "#aaa" }}>— Slot {i + 1} —</em>
                          </MenuItem>
                          {scoutReports.map((r) => (
                            <MenuItem key={r.id} value={r.id} disabled={selected.includes(r.id) && selected[i] !== r.id}>
                              {r.player} ({r.position})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SoftBox>
                  </Grid>
                ))}
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>

        {activePlayers.length < 2 ? (
          <Card>
            <SoftBox p={4} textAlign="center">
              <SoftTypography variant="body2" color="text">
                Select at least 2 players to begin comparison.
              </SoftTypography>
            </SoftBox>
          </Card>
        ) : (
          <>
            {/* Player header cards */}
            <SoftBox mb={3}>
              <Grid container spacing={2}>
                {players.map((player, i) =>
                  player ? (
                    <Grid key={i} item xs={12} sm={6} md={12 / activePlayers.length}>
                      <Card>
                        <SoftBox
                          p={2}
                          sx={{ borderTop: `4px solid ${PLAYER_COLORS[i]}` }}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1.5} mb={1}>
                            <SoftBox
                              width={44}
                              height={44}
                              borderRadius="50%"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ backgroundColor: PLAYER_COLORS[i] + "22", border: `2px solid ${PLAYER_COLORS[i]}` }}
                            >
                              <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PLAYER_COLORS[i], fontSize: "0.7rem" }}>
                                {player.player.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox>
                              <SoftTypography variant="button" fontWeight="bold" display="block" lineHeight={1.2}>
                                {player.player}
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                {player.flag} {player.club}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                          <SoftBox display="flex" gap={0.5} flexWrap="wrap">
                            <SoftBadge badgeContent={player.position} color="info" size="xs" container />
                            <SoftBadge badgeContent={`Age ${player.age}`} color="secondary" size="xs" container />
                            <SoftBadge
                              badgeContent={player.recommendation}
                              color={RECOMMENDATION_COLOR[player.recommendation]}
                              size="xs"
                              container
                            />
                          </SoftBox>
                          <SoftBox mt={1.5} display="flex" justifyContent="space-between" alignItems="center">
                            <SoftTypography variant="caption" color="text">Overall Rating</SoftTypography>
                            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PLAYER_COLORS[i] }}>
                              {player.overallRating}
                            </SoftTypography>
                          </SoftBox>
                          {player.feeEstimate && (
                            <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                              Est. fee: {formatCompact(player.feeEstimate)}
                            </SoftTypography>
                          )}
                        </SoftBox>
                      </Card>
                    </Grid>
                  ) : null
                )}
              </Grid>
            </SoftBox>

            {/* Attribute comparison table */}
            <Card>
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                  Attribute Comparison
                </SoftTypography>

                {ATTRIBUTES.map(({ key, label }) => {
                  const best = getBestForAttr(key);
                  return (
                    <SoftBox key={key} mb={2.5}>
                      <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                        {label.toUpperCase()}
                      </SoftTypography>
                      <Grid container spacing={1} alignItems="center">
                        {/* Label column */}
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {players.map((player, i) =>
                              player ? (
                                <Grid key={i} item xs={12 / activePlayers.length}>
                                  <SoftBox>
                                    <SoftBox display="flex" justifyContent="space-between" mb={0.3}>
                                      <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                        {player.player.split(" ").slice(-1)[0]}
                                      </SoftTypography>
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                          color: (player.attributes?.[key] ?? 0) === best ? PLAYER_COLORS[i] : "#aaa",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {player.attributes?.[key] ?? 0}
                                        {(player.attributes?.[key] ?? 0) === best && activePlayers.length > 1 && " ★"}
                                      </SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={{ height: 8, borderRadius: 4, backgroundColor: "#e9ecef", overflow: "hidden" }}>
                                      <SoftBox
                                        sx={{
                                          height: "100%",
                                          width: `${(player.attributes?.[key] ?? 0) * 10}%`,
                                          backgroundColor: PLAYER_COLORS[i],
                                          borderRadius: 4,
                                          opacity: (player.attributes?.[key] ?? 0) === best ? 1 : 0.45,
                                          transition: "width 0.4s ease",
                                        }}
                                      />
                                    </SoftBox>
                                  </SoftBox>
                                </Grid>
                              ) : null
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </Card>

            {/* Generate board report */}
            <SoftBox mt={3} display="flex" justifyContent="flex-end">
              <SoftButton variant="gradient" color="info">
                Generate Board Report
              </SoftButton>
            </SoftBox>
          </>
        )}
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default PlayerComparison;
